import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Building2, Search, Filter, Bookmark, BookmarkCheck, Kanban } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BrokerDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());

  useEffect(() => {
    fetchListings();
    fetchBookmarks();
  }, [token]);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/listings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ids = new Set(response.data.map(listing => listing.id));
      setBookmarkedIds(ids);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  const handleToggleBookmark = async (listingId) => {
    const isBookmarked = bookmarkedIds.has(listingId);
    
    try {
      if (isBookmarked) {
        await axios.delete(`${API_URL}/api/bookmarks/${listingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBookmarkedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(listingId);
          return newSet;
        });
        toast.success('Bookmark removed');
      } else {
        await axios.post(
          `${API_URL}/api/bookmarks`,
          { listing_id: listingId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBookmarkedIds(prev => new Set(prev).add(listingId));
        toast.success('Listing bookmarked');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bookmark');
    }
  };

  const filteredListings = listings
    .filter(listing =>
      listing.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      listing.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      listing.pincode.includes(searchQuery)
    )
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'price-high') {
        return b.price - a.price;
      } else {
        return a.price - b.price;
      }
    });

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)}Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)}L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="broker-title">
                All Listings
              </h1>
              <p className="text-lg text-slate-600">
                Browse and analyze properties from companies across India
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/broker/bookmarks')}
                className="bg-white border border-slate-200 text-slate-700 rounded-lg px-6 py-3 font-medium hover:bg-slate-50 smooth-transition flex items-center gap-2"
                data-testid="bookmarks-button"
              >
                <Bookmark className="w-5 h-5" />
                Bookmarks
              </button>
              <button
                onClick={() => navigate('/broker/crm')}
                className="bg-slate-900 text-white rounded-lg px-6 py-3 font-medium hover:bg-slate-800 smooth-transition flex items-center gap-2"
                data-testid="crm-button"
              >
                <Kanban className="w-5 h-5" />
                CRM
              </button>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, location, or pincode..."
              className="w-full h-12 pl-12 pr-4 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition"
              data-testid="search-input"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-12 pl-12 pr-10 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition appearance-none"
              data-testid="sort-select"
            >
              <option value="date">Latest First</option>
              <option value="price-high">Price: High to Low</option>
              <option value="price-low">Price: Low to High</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            <div className="text-slate-600">Loading listings...</div>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200" data-testid="empty-state">
            <div className="text-slate-600">No listings found</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="listings-container">
            {filteredListings.map((listing, index) => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 smooth-transition group"
                data-testid={`listing-${index}`}
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-64 h-48 md:h-auto bg-slate-100 flex-shrink-0">
                    {listing.image_url ? (
                      <img
                        src={listing.image_url}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building2 className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
                          {listing.title}
                        </h3>
                        <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{listing.location}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">PIN:</span>
                            <span>{listing.pincode}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(listing.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-3 ml-4">
                        <div className="text-2xl font-bold text-slate-900">
                          {formatPrice(listing.price)}
                        </div>
                        <button
                          onClick={() => handleToggleBookmark(listing.id)}
                          className={`p-2 rounded-lg smooth-transition ${
                            bookmarkedIds.has(listing.id)
                              ? 'bg-emerald-100 text-emerald-600'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`bookmark-button-${index}`}
                        >
                          {bookmarkedIds.has(listing.id) ? (
                            <BookmarkCheck className="w-5 h-5 fill-current" />
                          ) : (
                            <Bookmark className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <p className="text-slate-600 line-clamp-2 mb-3">
                      {listing.description}
                    </p>
                    
                    <div className="text-sm text-slate-500">
                      Listed by {listing.company_email}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};